// 平面圖模式（plan/vms-web-v0.md §5.2.3 FP-01 / 03 / 04 / 05）：樓層圖存在雲端（私有 S3，經授權取簽名網址）、
// 擺放與圖共用一個版本（兩人同時編輯，後存者 409 → 重新讀取），設備狀態與事件即時上色。
import { useMemo, useRef, useState, type MouseEvent as RME } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../components/ConfirmDialog';
import { KinioError } from '@kinio/web-sdk';
import { useDevices } from '../../lib/useDevices';
import { useScope } from '../../lib/scope';
import { useLiveEvents } from '../../lib/liveEvents';
import { fullName, type VmsDevice } from '../../lib/devices';
import { ownerKey, vmsApi, type ApiPlacement, type FloorplanOwner } from '../../lib/planeApi';
import { LiveTile } from '../live/LiveTile';
import { useFloorplanImage, type OwnedFloorplan } from './useMapData';
import { deviceGlyph, placementKey, upsertApiPlacement } from './placement';

/** 讀圖片尺寸（上傳時用）。 */
function imageSize(blob: Blob): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth || 1600, height: img.naturalHeight || 900 });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('無法讀取圖片'));
    };
    img.src = url;
  });
}

/** 範例平面圖的建議位置（demo）：依序給攝影機 / 感測器。座標 0..1，朝向 0 = 朝上。 */
const DEMO_SPOTS = [
  { x: 0.21, y: 0.86, heading_deg: 0, fov_deg: 100 },
  { x: 0.08, y: 0.1, heading_deg: 135, fov_deg: 90 },
  { x: 0.4, y: 0.12, heading_deg: 180, fov_deg: 90 },
  { x: 0.7, y: 0.45, heading_deg: 90, fov_deg: 80 },
  { x: 0.92, y: 0.9, heading_deg: 315, fov_deg: 90 },
  { x: 0.33, y: 0.55, heading_deg: 270, fov_deg: 90 },
];
const DEMO_OTHER_SPOTS = [
  { x: 0.08, y: 0.6 },
  { x: 0.08, y: 0.72 },
  { x: 0.08, y: 0.84 },
];

interface Props {
  plans: OwnedFloorplan[];
  writable: FloorplanOwner[];
  alerting: Map<string, number>;
  /** MAP-03：清單上點到的事件的設備（標記加框） */
  focusKey?: string;
  curId: string | null;
  setCurId(id: string | null): void;
}

export function FloorplanView({ plans, writable, alerting, focusKey, curId, setCurId }: Props) {
  const { t } = useTranslation();
  const { ask, dialog } = useConfirm();
  const qc = useQueryClient();
  const { tree, loading } = useDevices();
  const { visible } = useScope();
  const { sensors } = useLiveEvents();
  const [editing, setEditing] = useState(false);
  const [arming, setArming] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploadOwner, setUploadOwner] = useState<string>('');
  const fileRef = useRef<HTMLInputElement>(null);

  const devices = useMemo(() => tree.flatMap((d) => [d, ...d.children]), [tree]);
  const byKey = useMemo(() => new Map(devices.map((d) => [d.key, d])), [devices]);
  const cur = plans.find((p) => p.floorplan_id === curId) ?? plans[0];
  const img = useFloorplanImage(cur);
  const owner = writable.find((o) => ownerKey(o) === uploadOwner) ?? writable[0];

  // 這張圖能擺哪些設備：家戶平面圖 = 那個家戶；組織平面圖 = 該組織的企業家戶
  const placeable = useMemo(() => {
    if (!cur) return [];
    const homes =
      cur.owner.kind === 'home'
        ? new Set([cur.owner.homeId])
        : new Set(visible.filter((h) => h.org_id === (cur.owner as { orgId: string }).orgId).map((h) => h.home_id));
    return devices.filter((d) => homes.has(d.homeId) && (d.isCamera || d.children.length === 0));
  }, [cur, devices, visible]);

  const refresh = () => qc.invalidateQueries({ queryKey: ['floorplans'] });

  async function savePlacements(next: ApiPlacement[]) {
    if (!cur) return;
    setErr(null);
    try {
      await vmsApi.updateFloorplan(cur.owner, cur, {
        placements: next.map(({ home_id, device_ref, x, y, heading_deg, fov_deg, range_m }) => ({ home_id, device_ref, x, y, heading_deg, fov_deg, range_m })),
      });
    } catch (e) {
      setErr(e instanceof KinioError && e.status === 409 ? t('map.conflict') : String((e as Error).message ?? e));
    }
    await refresh();
  }

  async function uploadFile(file: Blob, name: string, o = owner): Promise<OwnedFloorplan | null> {
    if (!o) return null;
    setBusy(true);
    setErr(null);
    try {
      const size = await imageSize(file).catch(() => ({ width: 1600, height: 900 }));
      const fp = await vmsApi.createFloorplan(o, file, name, size.width, size.height);
      await refresh();
      setCurId(fp.floorplan_id);
      return { ...fp, owner: o, canWrite: true };
    } catch (e) {
      setErr(String((e as Error).message ?? e));
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function loadDemo() {
    const o = owner;
    if (!o) return;
    const blob = await (await fetch('/demo/floorplan-1f.svg')).blob();
    const fp = await uploadFile(new File([blob], 'demo.svg', { type: 'image/svg+xml' }), t('map.demo_name'), o);
    if (!fp) return;
    const homeIds = o.kind === 'home' ? [o.homeId] : visible.filter((h) => h.org_id === o.orgId).map((h) => h.home_id);
    const mine = devices.filter((d) => homeIds.includes(d.homeId));
    const online = mine.filter((d) => d.isCamera && d.presence === 'online');
    const cams = online.length ? online : mine.filter((d) => d.isCamera);
    const others = mine.filter((d) => !d.isCamera && d.children.length === 0 && (d.presence === 'online' || !online.length));
    const placements: ApiPlacement[] = [
      ...cams.slice(0, DEMO_SPOTS.length).map((d, i) => ({ home_id: d.homeId, device_ref: d.aid, ...DEMO_SPOTS[i] })),
      ...others.slice(0, DEMO_OTHER_SPOTS.length).map((d, i) => ({ home_id: d.homeId, device_ref: d.aid, ...DEMO_OTHER_SPOTS[i], heading_deg: 0 })),
    ];
    try {
      await vmsApi.updateFloorplan(o, fp, { placements });
    } catch (e) {
      setErr(String((e as Error).message ?? e));
    }
    await refresh();
  }


  function clickPlan(e: RME<HTMLDivElement>) {
    if (!cur || !editing || !arming) return;
    const d = byKey.get(arming);
    if (!d) return;
    const r = e.currentTarget.getBoundingClientRect();
    const existing = cur.placements.find((x) => placementKey(x) === arming);
    void savePlacements(
      upsertApiPlacement(cur.placements, {
        home_id: d.homeId,
        device_ref: d.aid,
        x: (e.clientX - r.left) / r.width,
        y: (e.clientY - r.top) / r.height,
        heading_deg: existing?.heading_deg ?? 0,
        fov_deg: existing?.fov_deg ?? (d.isCamera ? 90 : undefined),
      }),
    );
    setArming(null);
  }

  const rotate = (p: ApiPlacement, delta: number) => void savePlacements(upsertApiPlacement(cur!.placements, { ...p, heading_deg: p.heading_deg + delta }));
  const removePlacement = (key: string) => void savePlacements(cur!.placements.filter((x) => placementKey(x) !== key));

  async function removePlan() {
    if (!cur || !(await ask({ danger: true, title: t('map.confirm_delete', { name: cur.name }), action: t('common.action_delete') }))) return;
    try {
      await vmsApi.deleteFloorplan(cur.owner, cur);
      setCurId(null);
    } catch (e) {
      setErr(e instanceof KinioError && e.status === 409 ? t('map.conflict') : String((e as Error).message ?? e));
    }
    await refresh();
  }

  const openDev = open ? byKey.get(open) : undefined;

  return (
    <div className="map-body">
      <aside className="cam-list">
        <h2>{t('map.plans')}</h2>
        {plans.map((p) => (
          <button
            key={p.floorplan_id}
            className={`cam-item ${cur?.floorplan_id === p.floorplan_id ? 'on-wall' : ''}`}
            onClick={() => (setCurId(p.floorplan_id), setEditing(false))}
          >
            🗺 {p.name}
            <span className="muted small"> · {p.owner.label}</span>
          </button>
        ))}
        {writable.length > 0 && (
          <div className="fp-upload">
            {writable.length > 1 && (
              <select className="input" value={owner ? ownerKey(owner) : ''} onChange={(e) => setUploadOwner(e.target.value)} aria-label={t('map.owner')}>
                {writable.map((o) => (
                  <option key={ownerKey(o)} value={ownerKey(o)}>
                    {o.kind === 'org' ? '🏢 ' : '🏠 '}
                    {o.label}
                  </option>
                ))}
              </select>
            )}
            <button className="btn btn-sm" onClick={() => fileRef.current?.click()} disabled={busy}>
              {t('map.upload')}
            </button>
            <button className="btn btn-sm" onClick={() => void loadDemo()} disabled={loading || busy}>
              {t('map.load_demo')}
            </button>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml,image/webp"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void uploadFile(f, f.name.replace(/\.[^.]+$/, ''));
            e.target.value = '';
          }}
        />
        {editing && cur && (
          <>
            <h2>{t('map.place_devices')}</h2>
            <p className="muted small">{arming ? t('map.click_to_place') : t('map.pick_device')}</p>
            {placeable.map((d) => {
              const placed = cur.placements.some((x) => placementKey(x) === d.key);
              return (
                <button key={d.key} className={`cam-item ${arming === d.key ? 'on-wall' : ''}`} onClick={() => setArming(d.key)} title={d.aid}>
                  <span className={`dot ${d.presence}`} />
                  {fullName(d, tree)}
                  {placed && <span className="muted small"> ✓</span>}
                </button>
              );
            })}
          </>
        )}
      </aside>
      <div className="wall-area">
        <div className="toolbar">
          {cur && <span className="muted">{cur.name}</span>}
          <span className="spacer" />
          {cur?.canWrite && (
            <button className={`btn ${editing ? 'btn-primary' : ''}`} onClick={() => (setEditing((v) => !v), setArming(null))}>
              {editing ? t('map.done') : t('map.edit')}
            </button>
          )}
          {cur?.canWrite && editing && (
            <button className="btn" onClick={() => void removePlan()}>
              {t('map.delete')}
            </button>
          )}
        </div>
        {err && <p className="error">{err}</p>}
        {!cur ? (
          <div className="card planned">
            <p>{writable.length ? t('map.empty') : t('map.empty_readonly')}</p>
          </div>
        ) : (
          <div className="fp-wrap">
            <div
              className={`fp ${editing ? 'editing' : ''} ${arming ? 'arming' : ''}`}
              style={{ aspectRatio: `${cur.width} / ${cur.height}` }}
              onClick={clickPlan}
            >
              {img.data && <img src={img.data} alt={cur.name} draggable={false} />}
              {cur.placements.map((p) => {
                const key = placementKey(p);
                const d = byKey.get(key);
                const alertAt = alerting.get(key);
                return (
                  <div
                    key={key}
                    className={`fp-marker ${d?.presence ?? 'unknown'} ${alertAt ? 'alarm' : ''} ${focusKey === key ? 'focus' : ''} ${sensors.get(key)?.active ? 'sensor-on' : ''}`}
                    style={{ left: `${(p.x ?? 0) * 100}%`, top: `${(p.y ?? 0) * 100}%` }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (editing) setArming(key);
                      else if (d?.isCamera) setOpen(key);
                    }}
                    title={d ? fullName(d, tree) : key}
                    data-device={key}
                  >
                    <span className="pin-anchor">
                      {d?.isCamera && (
                        <span className="fov" style={{ transform: `rotate(${p.heading_deg}deg)`, ['--fov' as string]: `${p.fov_deg ?? 90}deg` }} />
                      )}
                      <span className="pin">{deviceGlyph(d)}</span>
                    </span>
                    <span className="label">{d ? fullName(d, tree) : '?'}</span>
                    {editing && (
                      <span className="fp-tools" onClick={(e) => e.stopPropagation()}>
                        {d?.isCamera && (
                          <>
                            <button className="icon-btn" aria-label={t('map.rotate_left')} onClick={() => rotate(p, -15)}>
                              ⟲
                            </button>
                            <button className="icon-btn" aria-label={t('map.rotate_right')} onClick={() => rotate(p, 15)}>
                              ⟳
                            </button>
                          </>
                        )}
                        <button className="icon-btn" aria-label={t('map.remove')} onClick={() => removePlacement(key)}>
                          ✕
                        </button>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            {openDev && (
              <div className="fp-popup">
                <LiveTile cam={openDev as VmsDevice} label={fullName(openDev, tree)} onClose={() => setOpen(null)} />
              </div>
            )}
          </div>
        )}
      </div>
      {dialog}
    </div>
  );
}
